import { createRef, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { MODAL_CANNED_RESPONSES } from '@/components/organisms/Modals/ModalConductor';
import { useMacros } from '@/hooks/useMacros';
import { cn } from '@/lib/utils';
import { setFocusIndex } from '@/redux/macros/actions';
import { focusIndexSelector } from '@/redux/macros/selectors';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { Macro } from '@/shared/types/macros';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import MacroRowLabel from './MacroRowLabel';
import SavedResponsesEmptyState from './SavedResponsesEmptyState';

interface Props {
  handleClick: (x: string) => void;
  filteredOptions: Partial<Macro>[];
}

const QuickResponses = ({ handleClick, filteredOptions }: Props) => {
  const { t } = useTranslation();
  const { macros, listStatus } = useMacros();
  const dispatch = useDispatch();
  const focusIndex = useSelector(focusIndexSelector);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'macros', actions.WRITE)
  );

  const optionRefs = useRef([]);
  optionRefs.current = filteredOptions.map((_, i) => optionRefs.current[i] ?? createRef());

  useEffect(() => {
    if (optionRefs.current[focusIndex]) {
      optionRefs.current[focusIndex].current?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusIndex]);

  if (!macros?.length && listStatus !== 'pending') {
    return (
      <SavedResponsesEmptyState
        canWrite={canWrite}
        onAdd={() => dispatch(pushModal(MODAL_CANNED_RESPONSES))}
      />
    );
  }

  return (
    <div className="absolute bottom-full left-0 z-10 mb-2 w-full rounded-lg border border-stroke bg-background shadow-none">
      <div className="max-h-64 overflow-y-auto p-3">
        {filteredOptions?.length > 0 &&
          filteredOptions.map(({ name, text, macro_id }, i) => (
            <div key={macro_id} ref={optionRefs.current[i]} className="rounded">
              <button
                className={cn(
                  'flex w-full flex-col gap-1 rounded px-2 py-1.5 text-left hover:bg-highlight-background',
                  focusIndex === i && 'bg-highlight-background'
                )}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleClick(text)}
                onMouseEnter={() => dispatch(setFocusIndex(i))}
              >
                <MacroRowLabel name={name} text={text} />
              </button>
            </div>
          ))}

        {filteredOptions?.length === 0 && listStatus !== 'pending' && (
          <div className="px-2 py-1.5 text-sm text-foreground-muted">
            {t('chatBox.no_response')}
          </div>
        )}

        {listStatus === 'pending' && (
          <div className="px-2 py-1.5 text-sm text-foreground-muted">{t('common.loading')}</div>
        )}
      </div>
    </div>
  );
};

export default QuickResponses;
